import {
	CreateSnippetRequest,
	CreateSnippetResponse,
	UpdateSnippetRequest,
	UpdateSnippetResponse,
	DeleteSnippetResponse,
	SnippetsSearchRequest,
	SnippetsSearchResponse,
	RateSnippetRequest,
	RateSnippetResponse,
	SnippetEntity
} from '@kemu-io/kemu-types';
import globals from '../../common/globals';
import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';

const API_NAME = globals.PLATFORM_API_NAME;

// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

/**
 * Creates a new snippet
 * @param snippetData The snippet data to create
 * @returns Promise containing the created snippet
 */
const createSnippet = async (snippetData: CreateSnippetRequest): Promise<CreateSnippetResponse> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/snippet`, { ...returnAxiosResponse, body: snippetData });
	return response.data as CreateSnippetResponse;
};

/**
 * Gets a specific version of a snippet
 * @param snippetId The ID of the snippet
 * @param version The version number to retrieve
 * @returns Promise containing the snippet data
 */
const getSnippetDetails = async (config: { 
	snippetId: string, 
	includeContents?: boolean,
	version?: number 
}): Promise<SnippetEntity> => {
	const { snippetId, version, includeContents } = config;
	const includeContentsParam = includeContents ? 'contents=true' : '';
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/snippet/${snippetId}/details/${version || 'latest'}?${includeContentsParam}`, returnAxiosResponse);
	return response.data as SnippetEntity;
};

const getSnippetContents = async (snippetId: string, version?: number): Promise<string> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/snippet/${snippetId}/contents/${version || 'latest'}`, returnAxiosResponse);
	return response.data as string;
};

/**
 * Updates an existing snippet
 * @param snippetId The ID of the snippet to update
 * @param snippetData The updated snippet data
 * @returns Promise containing the updated snippet
 */
const updateSnippet = async (snippetId: string, snippetData: UpdateSnippetRequest): Promise<UpdateSnippetResponse> => {
	const response = await clerkApiInterceptor.put(API_NAME, `/v1/snippet/${snippetId}`, { ...returnAxiosResponse, body: snippetData });
	return response.data as UpdateSnippetResponse;
};

/**
 * Deletes a snippet
 * @param snippetId The ID of the snippet to delete
 * @returns Promise containing the deletion result
 */
const deleteSnippet = async (snippetId: string): Promise<DeleteSnippetResponse> => {
	const response = await clerkApiInterceptor.delete(API_NAME, `/v1/snippet/${snippetId}`, returnAxiosResponse);
	return response.data as DeleteSnippetResponse;
};

/**
 * Searches for snippets
 * @param searchParams The search parameters
 * @returns Promise containing the search results
 */
const searchSnippets = async (searchParams: SnippetsSearchRequest): Promise<SnippetsSearchResponse> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/snippet/search`, { 
		...returnAxiosResponse, 
		params: searchParams 
	});
	return response.data as SnippetsSearchResponse;
};

/**
 * Rates a snippet from 0 to 5
 * @param snippetId The ID of the snippet to rate
 * @param ratingData The rating data
 * @returns Promise containing the rating response
 */
const rateSnippet = async (snippetId: string, ratingData: RateSnippetRequest): Promise<RateSnippetResponse> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/snippet/${snippetId}/rate`, { ...returnAxiosResponse, body: ratingData });
	return response.data as RateSnippetResponse;
};

export {
	createSnippet,
	getSnippetDetails,
	getSnippetContents,
	updateSnippet,
	deleteSnippet,
	searchSnippets,
	rateSnippet,
};
